import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    IoArrowForward, IoSparkles, IoGrid, IoSearch 
} from 'react-icons/io5';
import { RiCompassLine, RiStarLine, RiShoppingBag3Line } from 'react-icons/ri';
import { useCategories, useProducts } from '@/hooks/useProducts';
import Button from '@/components/common/Button/Button';
import Spinner from '@/components/common/Spinner/Spinner';
import ProductCard from '@/components/common/ProductCard/ProductCard';
import CategoryCard from '@/components/common/CategoryCard/CategoryCard';

import './Categories.css';

const Categories: React.FC = () => {
    const navigate = useNavigate();
    const { data: categories = [], isLoading: categoriesLoading } = useCategories();
    const { data: featuredProducts, isLoading: productsLoading } = useProducts({ 
        page_size: 6, 
        is_featured: true 
    });

    const handleCategoryClick = (categoryId: number) => {
        navigate(`/products?category=${categoryId}`);
    };

    if (categoriesLoading) {
        return (
            <div className="categories_loading_container">
                <div className="categories_loading_content">
                    <Spinner size="lg" />
                    <p className="categories_loading_text">Cargando categorías...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="categories_main_container">
            {/* Hero Section */}
            <div className="categories_hero_section">
                <div className="categories_hero_background"></div>
                <div className="categories_hero_overlay"></div>
                <div className="categories_hero_content">
                    <div className="categories_hero_badge">
                        <IoSparkles size={16} />
                        <span className="categories_badge_text">Explora Nuestras Categorías</span>
                    </div>
                    <h1 className="categories_hero_title">
                        Descubre la <span className="categories_gradient_text">Variedad</span> Perfecta
                    </h1>
                    <p className="categories_hero_subtitle">
                        Navega por nuestra amplia gama de categorías de productos plásticos premium. 
                        Desde contenedores personalizados hasta soluciones industriales especializadas.
                    </p>
                    <div className="categories_hero_stats">
                        <div className="categories_stat_item">
                            <IoGrid className="categories_stat_icon" />
                            <div className="categories_stat_content">
                                <div className="categories_stat_number">{categories?.length || 0}</div>
                                <div className="categories_stat_label">Categorías Disponibles</div>
                            </div>
                        </div>
                        <div className="categories_stat_item">
                            <RiShoppingBag3Line className="categories_stat_icon" />
                            <div className="categories_stat_content">
                                <div className="categories_stat_number">500+</div>
                                <div className="categories_stat_label">Productos Únicos</div>
                            </div>
                        </div>
                        <div className="categories_stat_item">
                            <RiStarLine className="categories_stat_icon" />
                            <div className="categories_stat_content">
                                <div className="categories_stat_number">4.9</div>
                                <div className="categories_stat_label">Calificación Promedio</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Categories Grid */}
            <div className="categories_grid_section">
                <div className="categories_section_header">
                    <h2 className="categories_section_title">Explora Nuestras Categorías</h2>
                    <p className="categories_section_subtitle">
                        Encuentra exactamente lo que necesitas navegando por nuestras categorías especializadas
                    </p>
                </div>

                <div className="categories_grid_container">
                    {categories.length > 0 ? (
                        <div className="categories_grid">
                            {categories.map((category, index) => (
                                <CategoryCard
                                    key={category.id}
                                    category={category}
                                    onClick={() => handleCategoryClick(category.id)}
                                    animationDelay={index * 0.1}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="categories_empty_state">
                            <div className="categories_empty_content">
                                <RiCompassLine className="categories_empty_icon" />
                                <h3 className="categories_empty_title">No se encontraron categorías</h3>
                                <p className="categories_empty_message">
                                    Estamos trabajando para agregar más categorías pronto.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Featured Products Section */}
            {featuredProducts?.results && featuredProducts.results.length > 0 && (
                <div className="categories_featured_section">
                    <div className="categories_section_header">
                        <h2 className="categories_section_title">Productos Destacados</h2>
                        <p className="categories_section_subtitle">
                            Descubre nuestros productos más populares y mejor valorados
                        </p>
                    </div>

                    <div className="categories_featured_container">
                        {productsLoading ? (
                            <div className="categories_loading_container">
                                <Spinner size="lg" />
                                <p className="categories_loading_text">Cargando productos...</p>
                            </div>
                        ) : (
                            <div className="categories_featured_grid">
                                {featuredProducts?.results?.slice(0, 6).map((product, index) => (
                                    <ProductCard
                                        key={product.id}
                                        product={product}
                                        variant="featured"
                                        animationDelay={index * 0.1}
                                    />
                                )) || []}
                            </div>
                        )}
                    </div>

                    <div className="categories_featured_actions">
                        <Button 
                            variant="primary" 
                            size="lg"
                            onClick={() => navigate('/products')}
                        >
                            <IoArrowForward size={20} />
                            Ver Todos los Productos
                        </Button>
                    </div>
                </div>
            )}

            {/* Call to Action Section */}
            <div className="categories_cta_section">
                <div className="categories_cta_content">
                    <div className="categories_cta_text">
                        <h2 className="categories_cta_title">¿Listo para Comenzar tu Proyecto?</h2>
                        <p className="categories_cta_subtitle">
                            Explora nuestro catálogo completo o contáctanos para soluciones personalizadas
                        </p>
                    </div>
                    <div className="categories_cta_actions">
                        <Button 
                            variant="primary" 
                            size="lg"
                            onClick={() => navigate('/products')}
                        >
                            <IoSearch size={20} />
                            Comenzar a Comprar
                        </Button>
                        <Button 
                            variant="secondary" 
                            size="lg"
                            onClick={() => navigate('/contact')}
                        >
                            Contáctanos
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Categories;
